import { Alert, Box, Button, Chip, Collapse, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { CheckCircleOutline, EditOutlined, ExpandLessOutlined, ExpandMoreOutlined, HighlightOff, OpenInNewOutlined } from '@mui/icons-material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import { FunctionComponent, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react'
import FileUploadService from '../../../../shared/services/file-upload/file-upload.service'
import FileUploadAllLineService from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.service'
import { IBillDocument, IBillExtractionProgress } from '../../../../shared/services/file-upload/file-upload.model'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model'
import { openBillDocument } from '../../../../shared/utils/open-bill-document'
import { billLineActivity, billLinePeriod, billLineUsage } from '../../../../shared/utils/bill-upload-utils'
import {
  IInlineValues,
  billReviewHeadline,
  buildInlineEditPayload,
  countBillTasks,
  countBills,
  coversAllUsage,
  findRenewableJob,
  groupBillRows,
  hasRenewableRate,
  inlineValuesProblem,
  isElectricityLine,
  isLiveDuplicate,
  normaliseValidationErrors,
  plural,
} from '../../../../shared/utils/bill-review-tasks'
import { TableDisplay } from '../../miscellaneous-components/table-display'
import { LoadingSpinner } from '../../display-elements-components/loading-spinner'
import { EditUploadLineEmissionDialog } from '../../../dialogs/settings/data-import/edit-upload-line-emission-dialog'
import { EditBulkUploadLinesDialog } from '../../../dialogs/settings/data-import/edit-bulk-upload-lines'

/**
 * BillReview
 * -------------------------------------------------------------------------
 * The review of a bill upload as a to-do list: what has to happen before the rows can import, one
 * numbered job per kind of problem, each with its own buttons, and the rows that are ready folded
 * away below. Missing usage and dates are typed straight into the row, with the bill a click away.
 *
 * Replaced a pair of tables (bills, then rows) with competing counts that left even an experienced
 * user unsure what to do (October 2026). Used on the import wizard's review step and on the upload's
 * Pending tab in import history; both import with their own button.
 */

// Every row still waiting in one upload. A bill upload is at most a few hundred rows.
const MAX_ROWS = 1000

const BULK_EDIT_MENU_ITEMS: Record<string, string[]> = {
  'emissions-rules': ['EDIT_RULE', 'EDIT_UNITS_CONSUMED', 'EDIT_START_DATE', 'EDIT_END_DATE', 'EDIT_SUBSIDIARY', 'EDIT_COUNTRY', 'EDIT_LOCATION', 'EDIT_SUPPLIER', 'EDIT_VEHICLE', 'EDIT_PROJECT', 'EDIT_REGULATED_EMISSION_TRADING_SCHEME'],
  'emissions-manual': ['EDIT_CONVERSION', 'EDIT_UNITS_CONSUMED', 'EDIT_START_DATE', 'EDIT_END_DATE', 'EDIT_SUBSIDIARY', 'EDIT_COUNTRY', 'EDIT_LOCATION', 'EDIT_SUPPLIER', 'EDIT_VEHICLE', 'EDIT_PROJECT', 'EDIT_REGULATED_EMISSION_TRADING_SCHEME', 'EDIT_SUPPLIER_EMISSION_RATE'],
}

const lineSummary = (line: any) => [billLineActivity(line), billLineUsage(line), billLinePeriod(line)].filter(Boolean).join(' · ')

const lineUnit = (line: any): string => line?.measurementUnitName || line?.rule?.measurementUnitName || ''

const initialInlineValues = (line: any): IInlineValues => ({
  unitsConsumed: line?.unitsConsumed || line?.unitsConsumed === 0 ? String(line.unitsConsumed) : '',
  startDate: line?.startDate ? moment(line.startDate).format('YYYY-MM-DD') : null,
  endDate: line?.endDate ? moment(line.endDate).format('YYYY-MM-DD') : null,
})

const TaskCard: FunctionComponent<{
  number?: number
  title: string
  description?: ReactNode
  action?: ReactNode
  children: ReactNode
}> = ({ number, title, description, action, children }) => (
  <Paper variant='outlined' component='section' aria-label={title} sx={{ p: { xs: 2, sm: 3 }, borderRadius: '10px' }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2}>
      <Stack direction='row' alignItems='center' gap={1.5} sx={{ minWidth: 0 }}>
        {number !== undefined && (
          <Box
            aria-hidden
            sx={{
              width: 28, height: 28, flexShrink: 0, borderRadius: '50%', border: 1, borderColor: 'primary.main',
              color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Typography variant='body2' sx={{ fontWeight: 500 }}>{number}</Typography>
          </Box>
        )}
        <Typography variant='subtitle1' component='h3' sx={{ fontWeight: 500 }}>{title}</Typography>
      </Stack>
      {action}
    </Stack>
    {description && (
      <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1 }}>{description}</Typography>
    )}
    <Box sx={{ mt: 2 }}>{children}</Box>
  </Paper>
)

const TaskItem: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
  <Box sx={{ py: 2, borderTop: 1, borderColor: 'divider', '&:first-of-type': { borderTop: 0, pt: 0 }, '&:last-of-type': { pb: 0 } }}>
    {children}
  </Box>
)

const BillName: FunctionComponent<{ name: string, summary?: string, note?: ReactNode }> = ({ name, summary, note }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant='body2' sx={{ fontWeight: 500, wordBreak: 'break-word' }}>{name}</Typography>
    {summary && <Typography variant='body2' sx={{ color: 'text.secondary' }}>{summary}</Typography>}
    {note && <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>{note}</Typography>}
  </Box>
)

const ActionRow: FunctionComponent<{ children: ReactNode }> = ({ children }) => (
  <Stack direction='row' gap={1} flexWrap='wrap' sx={{ mt: 1.5 }}>{children}</Stack>
)

const ViewBillButton: FunctionComponent<{ onClick: () => void, label?: string }> = ({ onClick, label = 'View bill' }) => (
  <Button size='small' variant='outlined' startIcon={<OpenInNewOutlined fontSize='small' />} onClick={onClick}>
    <Typography variant='body2' sx={{ pl: 0.5 }}>{label}</Typography>
  </Button>
)

/** Usage and dates for one row, typed in from the bill. Nothing is imported on save. */
const InlineFigures: FunctionComponent<{
  line: any
  disabled: boolean
  isSaving: boolean
  onSave: (values: IInlineValues) => void
}> = ({ line, disabled, isSaving, onSave }) => {
  const [values, setValues] = useState<IInlineValues>(() => initialInlineValues(line))
  const [openPicker, setOpenPicker] = useState<'start' | 'end' | null>(null)
  const problem = inlineValuesProblem(values)
  const unit = lineUnit(line)
  const setDate = (field: 'startDate' | 'endDate', date: moment.Moment | null) =>
    setValues((current) => ({ ...current, [field]: date && date.isValid() ? date.format('YYYY-MM-DD') : null }))

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
        <TextField
          size='small'
          label='Usage'
          value={values.unitsConsumed}
          onChange={(event) => setValues((current) => ({ ...current, unitsConsumed: event.target.value }))}
          error={values.unitsConsumed.trim() === ''}
          disabled={disabled}
          inputProps={{ inputMode: 'decimal', 'aria-label': 'Usage' }}
          InputProps={unit ? { endAdornment: <InputAdornment position='end'>{unit}</InputAdornment> } : undefined}
          sx={{ width: { xs: '100%', md: 200 } }}
        />
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            label='Start date'
            value={values.startDate ? moment(values.startDate) : null}
            onChange={(date) => setDate('startDate', date)}
            format='DD/MM/YYYY'
            disabled={disabled}
            open={openPicker === 'start'}
            onOpen={() => setOpenPicker('start')}
            onClose={() => setOpenPicker(null)}
            slotProps={{ textField: { size: 'small', error: !values.startDate, onClick: () => !disabled && setOpenPicker('start'), sx: { width: { xs: '100%', md: 170 } } } }}
          />
          <DatePicker
            label='End date'
            value={values.endDate ? moment(values.endDate) : null}
            onChange={(date) => setDate('endDate', date)}
            format='DD/MM/YYYY'
            disabled={disabled}
            open={openPicker === 'end'}
            onOpen={() => setOpenPicker('end')}
            onClose={() => setOpenPicker(null)}
            referenceDate={values.startDate ? moment(values.startDate) : undefined}
            slotProps={{ textField: { size: 'small', error: !values.endDate, onClick: () => !disabled && setOpenPicker('end'), sx: { width: { xs: '100%', md: 170 } } } }}
          />
        </LocalizationProvider>
        <LoadingButton
          variant='contained'
          disabled={disabled || !!problem}
          loading={isSaving}
          onClick={() => onSave(values)}
          sx={{ minWidth: 80, height: 40 }}
        >
          <Typography>Save</Typography>
        </LoadingButton>
      </Stack>
      {problem && !disabled && (
        <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>{problem}</Typography>
      )}
    </Box>
  )
}

export const BillReview: FunctionComponent<{
  fileUploadId: string
  /** The upload's import type: 'emissions-rules' or 'emissions-manual'. */
  fileType: string
  reportPeriodId?: string | null
  documents: IBillDocument[]
  progress?: IBillExtractionProgress | null
  isReading: boolean
  /** Bump to refetch the rows, after the caller changed them. */
  refreshKey?: number
  /** After anything here changes the rows or the bills, so the caller can refresh its own counts. */
  onChanged: () => void
  /** Where the review is: the wizard imports with its own button below; the history page passes one. */
  context: 'wizard' | 'history'
  /** A locked period: the list without the buttons. */
  readOnly?: boolean
  /** The caller's own buttons under the list (the import button), given how many rows will import. */
  renderFooter?: (importableCount: number) => ReactNode
}> = ({ fileUploadId, fileType, reportPeriodId, documents, progress, isReading, refreshKey = 0, onChanged, context, readOnly = false, renderFooter }) => {
  const fileUploadServiceRef = useRef(new FileUploadService())
  const lineServiceRef = useRef(new FileUploadAllLineService())
  const userNotificationsContext = useContext(UserNotificationsContext)
  const reportPeriodContext = useContext(ReportPeriodContext)

  const [lines, setLines] = useState<any[]>([])
  const [lineCount, setLineCount] = useState(0)
  const [hasLoadedLines, setHasLoadedLines] = useState(false)
  const [reloadCount, setReloadCount] = useState(0)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [editLine, setEditLine] = useState<any>(null)
  const [isEditSubmitting, setIsEditSubmitting] = useState(false)
  const [showReady, setShowReady] = useState<boolean | null>(null)
  const [showCreditNotes, setShowCreditNotes] = useState(false)
  const [selectedReady, setSelectedReady] = useState<string[]>([])
  const [readyPerPage, setReadyPerPage] = useState(10)
  const [openBulkEdit, setOpenBulkEdit] = useState(false)

  useEffect(() => {
    // The rows only exist once every bill has been read.
    if (isReading) return
    let cancelled = false
    lineServiceRef.current.getUploadLinesWithPaginationByFile(fileType, fileUploadId, 'uncommitted', 0, MAX_ROWS)
      .then((response) => {
        if (cancelled) return
        setLines(response?.rows || [])
        setLineCount(response?.count || 0)
        setHasLoadedLines(true)
      })
      .catch((error) => {
        console.log(error)
        if (!cancelled) setHasLoadedLines(true)
      })
    return () => { cancelled = true }
  }, [fileUploadId, fileType, refreshKey, reloadCount, isReading])

  const documentsById = useMemo(() => {
    const map: Record<string, IBillDocument> = {}
    documents.forEach((document) => { map[document.id] = document })
    return map
  }, [documents])

  const groups = useMemo(() => groupBillRows(lines, documentsById), [lines, documentsById])
  // Bills that say their electricity is renewable and backed by certificates: a job, never a block.
  const renewableJob = useMemo(() => findRenewableJob(documents, groups), [documents, groups])
  const renewableBills = [...renewableJob.confirmable, ...renewableJob.partial]
  const taskCount = countBillTasks(groups) + (renewableBills.length > 0 ? 1 : 0)
  // Possible duplicates import with the ready rows unless one copy is discarded: a warning, never a block.
  const importable = useMemo(() => [...groups.ready, ...groups.duplicates], [groups])
  const creditNotes = documents.filter((document) => !!document.isCreditNote)
  const reportPeriod = reportPeriodContext?.allReportPeriods?.find((period: any) => period.id === reportPeriodId)

  // The ready rows start folded while there is anything to do, and open once the list is clear.
  const isReadyOpen = showReady ?? taskCount === 0

  const changed = () => {
    setReloadCount((count) => count + 1)
    onChanged()
  }

  const snackError = (error: any) => {
    const code = error?.response?.data?.code
    userNotificationsContext.openSnackbar({
      open: true,
      message: code === 100 || code === 1 ? 'Report period is locked' : error?.response?.data?.message || 'There was an error, please try again',
      backgroundColor: 'text.primary',
    })
  }

  const run = async (key: string, action: () => Promise<any>, successMessage: string) => {
    if (busyKey) return
    setBusyKey(key)
    try {
      await action()
      userNotificationsContext.openSnackbar({ open: true, message: successMessage })
      changed()
    } catch (error: any) {
      snackError(error)
    } finally {
      setBusyKey(null)
    }
  }

  const confirm = (title: string, message: string, buttonCopy: string) =>
    userNotificationsContext.openDialog({ active: true, title, message, buttonCopy, cancelButtonCopy: 'Cancel' })

  const linesOfBill = (billDocumentId: string) => lines.filter((line) => line.billDocumentId === billDocumentId)

  const discard = async (key: string, toDiscard: any[], title: string, message: string, successMessage: string) => {
    if (toDiscard.length === 0) return
    if (!(await confirm(title, message, 'Discard'))) return
    await run(key, () => lineServiceRef.current.deleteUploadLines(toDiscard.map((line) => ({ id: line.id })) as any, fileType), successMessage)
    setSelectedReady([])
  }

  const viewBill = async (billDocumentId?: string | null) => {
    if (!billDocumentId) return
    const opened = await openBillDocument(billDocumentId)
    if (!opened) {
      userNotificationsContext.openSnackbar({ open: true, message: 'Could not open this bill. Please try again.', backgroundColor: 'text.primary' })
    }
  }

  const saveInline = (line: any, values: IInlineValues) =>
    run(`save-${line.id}`, () => lineServiceRef.current.editUploadLine(buildInlineEditPayload(line, values) as any, line.id, fileType), 'Saved')

  const submitEdit = async (lineId: string, payload: any) => {
    if (isEditSubmitting) return
    setIsEditSubmitting(true)
    try {
      // No create flag: a bill row is only ever imported by the import button.
      await lineServiceRef.current.editUploadLine(payload, lineId, fileType)
      userNotificationsContext.openSnackbar({ open: true, message: 'Saved' })
      setEditLine(null)
      changed()
    } catch (error: any) {
      snackError(error)
    } finally {
      setIsEditSubmitting(false)
    }
  }

  const nameOf = (line: any) => documentsById[line.billDocumentId]?.filename || 'Bill'

  const decideRenewable = (billDocumentId: string, decision: 'confirm' | 'dismiss') =>
    fileUploadServiceRef.current.decideBillDocumentRenewable(billDocumentId, decision)

  const confirmRenewable = async (document: IBillDocument) => {
    const confirmed = await confirm(
      'Confirm renewable electricity',
      `The market-based emissions for ${document.filename} will be recorded as 0 kgCO2e. Only confirm if its renewable tariff covers these months.`,
      'Confirm',
    )
    if (confirmed) await run(`renewable-${document.id}`, () => decideRenewable(document.id, 'confirm'), 'Confirmed as renewable')
  }

  const confirmAllRenewable = async () => {
    const bills = renewableJob.confirmable
    if (bills.length === 0) return
    const confirmed = await confirm(
      `Confirm renewable electricity on ${plural(bills.length, 'bill', 'bills')}`,
      `Their market-based emissions will be recorded as 0 kgCO2e. Only confirm if each bill's renewable tariff covers its months.`,
      'Confirm',
    )
    if (!confirmed) return
    await run('renewable-all', async () => {
      for (const document of bills) {
        await decideRenewable(document.id, 'confirm')
      }
    }, `Confirmed ${plural(bills.length, 'bill', 'bills')} as renewable`)
  }

  const isBusy = !!busyKey || readOnly

  if (isReading) {
    const done = progress ? progress.extractedDocuments : documents.filter((document) => document.extractionStatus !== 'PENDING' && document.extractionStatus !== 'IN_PROGRESS').length
    return (
      <Alert severity='info'>
        Reading your bills: {done.toLocaleString()} of {documents.length.toLocaleString()} done. Your list of things to check
        appears here once every bill has been read. You can leave this page and come back.
      </Alert>
    )
  }

  if (!hasLoadedLines) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
        <LoadingSpinner />
      </Box>
    )
  }

  const guidance = (() => {
    if (lines.length === 0) {
      if (context === 'history') return 'Nothing from this upload is waiting to import.'
      if (documents.length > 0 && creditNotes.length === documents.length) {
        return 'These documents are all credit notes, so there is nothing to import. A credit note reverses a bill you were charged in error: if the original bill was already imported, edit or remove that emission instead.'
      }
      return 'No rows could be read from your bills.'
    }
    if (taskCount === 0) return 'Nothing needs fixing. Give the rows a quick look against the bills, then import them.'
    const later = context === 'wizard' ? 'Anything you leave can be finished later in Import History.' : 'Anything you leave stays on this list.'
    return `${taskCount === 1 ? 'There is 1 thing' : `There are ${taskCount} things`} to do first. Work through ${taskCount === 1 ? 'it' : 'them'} below, then import the ready rows. ${later}`
  })()

  let taskNumber = 0
  const cancelledBills = countBills(groups.cancelled)
  const missingBills = countBills(groups.missing)
  const outsideBills = countBills(groups.outsidePeriod)

  const readyHeadCells: IHeadCell[] = [
    { id: 'bill', numeric: false, label: 'Bill', sortable: false },
    { id: 'activity', numeric: false, label: 'Activity', sortable: false },
    { id: 'usage', numeric: false, label: 'Usage', sortable: false },
    { id: 'period', numeric: false, label: 'Period', sortable: false },
    { id: 'actions', numeric: false, label: '', sortable: false },
  ]
  const readyRows = importable.map((line) => {
    const document = documentsById[line.billDocumentId]
    return {
      id: line.id,
      bill: (
        <Stack spacing={0.5} alignItems='flex-start'>
          <Typography variant='body2' sx={{ wordBreak: 'break-word' }}>{document?.filename || ''}</Typography>
          {isLiveDuplicate(document, documentsById) && <Chip size='small' variant='outlined' color='warning' label='Possible duplicate' />}
          {hasRenewableRate(line) && <Chip size='small' variant='outlined' color='success' label='Renewable: 0 market-based' />}
        </Stack>
      ),
      activity: billLineActivity(line),
      usage: billLineUsage(line),
      period: billLinePeriod(line),
      actions: (
        <Stack direction='row' gap={1} justifyContent='flex-end' sx={{ whiteSpace: 'nowrap' }}>
          <Button size='small' variant='text' onClick={() => viewBill(line.billDocumentId)} startIcon={<OpenInNewOutlined fontSize='small' />}>
            <Typography variant='body2' sx={{ pl: 0.5 }}>View bill</Typography>
          </Button>
          {!readOnly && (
            <Button size='small' variant='text' onClick={() => setEditLine(line)} startIcon={<EditOutlined fontSize='small' />}>
              <Typography variant='body2' sx={{ pl: 0.5 }}>Edit</Typography>
            </Button>
          )}
        </Stack>
      ),
    }
  })

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant='body1' sx={{ fontWeight: 500, mb: 0.5 }}>{billReviewHeadline(documents.length, importable.length)}</Typography>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>{guidance}</Typography>
        {lineCount > lines.length && (
          <Alert severity='info' sx={{ mt: 2 }}>
            Showing the first {lines.length.toLocaleString()} of {lineCount.toLocaleString()} rows.
          </Alert>
        )}
      </Box>

      <Stack spacing={2}>
        {groups.cancelled.length > 0 && (
          <TaskCard
            number={++taskNumber}
            title={`Discard ${plural(cancelledBills, 'cancelled bill', 'cancelled bills')}`}
            description='A credit note in this upload cancels each of these bills, and the bill that replaces it is already here. Importing both would count the same period twice.'
            action={!readOnly && (
              <LoadingButton
                variant='contained'
                startIcon={<HighlightOff />}
                loading={busyKey === 'discard-cancelled'}
                loadingPosition='start'
                disabled={isBusy}
                onClick={() => discard(
                  'discard-cancelled',
                  groups.cancelled,
                  `Discard ${plural(cancelledBills, 'cancelled bill', 'cancelled bills')}`,
                  `${cancelledBills === 1 ? 'Its rows move' : 'Their rows move'} to Discarded. The bills that replace ${cancelledBills === 1 ? 'it' : 'them'} are not affected.`,
                  `Discarded ${plural(cancelledBills, 'cancelled bill', 'cancelled bills')}`,
                )}
              >
                <Typography>{groups.cancelled.length === 1 ? 'Discard it' : `Discard all ${cancelledBills}`}</Typography>
              </LoadingButton>
            )}
          >
            {groups.cancelled.map((line) => {
              const document = documentsById[line.billDocumentId]
              return (
                <TaskItem key={line.id}>
                  <BillName
                    name={nameOf(line)}
                    summary={lineSummary(line)}
                    note={document?.cancelledBy ? `Cancelled by ${document.cancelledBy.filename}` : undefined}
                  />
                  <ActionRow>
                    <ViewBillButton onClick={() => viewBill(line.billDocumentId)} />
                    {document?.cancelledByBillDocumentId && (
                      <ViewBillButton label='View credit note' onClick={() => viewBill(document.cancelledByBillDocumentId)} />
                    )}
                    {!readOnly && document?.cancelledByBillDocumentId && (
                      <Button
                        size='small'
                        variant='text'
                        disabled={isBusy}
                        onClick={async () => {
                          const confirmed = await confirm(
                            'Not cancelled by this credit note',
                            `${document.filename} will no longer be held back by ${document.cancelledBy?.filename || 'the credit note'}, and its rows can import. Only do this if the credit note cancels a different bill.`,
                            'Keep this bill',
                          )
                          if (confirmed) await run(`release-${document.id}`, () => fileUploadServiceRef.current.dismissBillDocumentCancellation(document.id), 'This bill is no longer held back')
                        }}
                      >
                        <Typography variant='body2'>Not cancelled</Typography>
                      </Button>
                    )}
                  </ActionRow>
                </TaskItem>
              )
            })}
          </TaskCard>
        )}

        {groups.missing.length > 0 && (
          <TaskCard
            number={++taskNumber}
            title={`Fill in what we couldn't read on ${plural(missingBills, 'bill', 'bills')}`}
            description='Open each bill, copy in the usage and the dates it covers, and save. The row joins the ready list once it is complete.'
          >
            {groups.missing.map((line) => {
              const document = documentsById[line.billDocumentId]
              return (
                <TaskItem key={line.id}>
                  <BillName
                    name={nameOf(line)}
                    summary={billLineActivity(line)}
                    note={document?.extractionStatus === 'FAILED' ? "We couldn't read this bill at all." : undefined}
                  />
                  {readOnly
                    ? <Typography variant='body2' sx={{ mt: 1 }}>{lineSummary(line)}</Typography>
                    : <InlineFigures line={line} disabled={isBusy} isSaving={busyKey === `save-${line.id}`} onSave={(values) => saveInline(line, values)} />}
                  <ActionRow>
                    <ViewBillButton onClick={() => viewBill(line.billDocumentId)} />
                    {!readOnly && (
                      <>
                        <Button size='small' variant='text' disabled={isBusy} startIcon={<EditOutlined fontSize='small' />} onClick={() => setEditLine(line)}>
                          <Typography variant='body2' sx={{ pl: 0.5 }}>More options</Typography>
                        </Button>
                        <Button
                          size='small'
                          variant='text'
                          disabled={isBusy}
                          onClick={() => discard(`discard-${line.id}`, [line], 'Discard this row', `The row from ${nameOf(line)} moves to Discarded and will not be imported.`, 'Discarded')}
                        >
                          <Typography variant='body2'>Discard</Typography>
                        </Button>
                      </>
                    )}
                  </ActionRow>
                </TaskItem>
              )
            })}
          </TaskCard>
        )}

        {groups.outsidePeriod.length > 0 && (
          <TaskCard
            number={++taskNumber}
            title={`Discard ${plural(outsideBills, 'bill', 'bills')} outside this reporting period`}
            description={`${reportPeriod ? `This reporting period runs from ${moment(reportPeriod.startDate).format('DD/MM/YYYY')} to ${moment(reportPeriod.endDate).format('DD/MM/YYYY')}. ` : ''}These bills are for dates outside it, so they belong in another period. Discard them here, or correct the dates if we misread them.`}
            action={!readOnly && (
              <LoadingButton
                variant='contained'
                startIcon={<HighlightOff />}
                loading={busyKey === 'discard-outside'}
                loadingPosition='start'
                disabled={isBusy}
                onClick={() => discard(
                  'discard-outside',
                  groups.outsidePeriod,
                  `Discard ${plural(outsideBills, 'bill', 'bills')} outside this reporting period`,
                  `${outsideBills === 1 ? 'Its rows move' : 'Their rows move'} to Discarded and will not be imported into this period.`,
                  `Discarded ${plural(outsideBills, 'bill', 'bills')}`,
                )}
              >
                <Typography>{groups.outsidePeriod.length === 1 ? 'Discard it' : `Discard all ${outsideBills}`}</Typography>
              </LoadingButton>
            )}
          >
            {groups.outsidePeriod.map((line) => (
              <TaskItem key={line.id}>
                <BillName name={nameOf(line)} summary={lineSummary(line)} />
                {!readOnly && (
                  <InlineFigures line={line} disabled={isBusy} isSaving={busyKey === `save-${line.id}`} onSave={(values) => saveInline(line, values)} />
                )}
                <ActionRow>
                  <ViewBillButton onClick={() => viewBill(line.billDocumentId)} />
                  {!readOnly && (
                    <Button
                      size='small'
                      variant='text'
                      disabled={isBusy}
                      onClick={() => discard(`discard-${line.id}`, [line], 'Discard this row', `The row from ${nameOf(line)} moves to Discarded and will not be imported.`, 'Discarded')}
                    >
                      <Typography variant='body2'>Discard</Typography>
                    </Button>
                  )}
                </ActionRow>
              </TaskItem>
            ))}
          </TaskCard>
        )}

        {groups.fix.length > 0 && (
          <TaskCard
            number={++taskNumber}
            title={`Fix ${plural(groups.fix.length, 'row', 'rows')} that can't import yet`}
            description='Open each row to correct what is flagged, with the bill open beside it.'
          >
            {groups.fix.map((line) => {
              const errors = normaliseValidationErrors(line.validationErrors)
              const unreadable = documentsById[line.billDocumentId]?.extractionStatus === 'FAILED'
              // A bill we could not read at all fails every check; one plain line says what to do.
              const messages = unreadable
                ? ["We couldn't read this bill at all. Open it, then choose the activity and enter the usage and dates."]
                : errors.length > 0
                  ? errors.map((error: any) => error?.message || 'Validation failed')
                  : ['This row failed to import. Open it to check the values, then import again.']
              return (
                <TaskItem key={line.id}>
                  <BillName name={nameOf(line)} summary={lineSummary(line)} />
                  {messages.length === 1
                    ? <Typography variant='body2' sx={{ mt: 1 }}>{messages[0]}</Typography>
                    : (
                      <Box component='ul' sx={{ m: 0, mt: 1, pl: 2.5 }}>
                        {messages.map((message: string, index: number) => (
                          <Typography key={index} component='li' variant='body2'>{message}</Typography>
                        ))}
                      </Box>
                    )}
                  <ActionRow>
                    <ViewBillButton onClick={() => viewBill(line.billDocumentId)} />
                    {!readOnly && (
                      <>
                        <Button size='small' variant='contained' disabled={isBusy} startIcon={<EditOutlined fontSize='small' />} onClick={() => setEditLine(line)}>
                          <Typography variant='body2' sx={{ pl: 0.5 }}>Fix</Typography>
                        </Button>
                        <Button
                          size='small'
                          variant='text'
                          disabled={isBusy}
                          onClick={() => discard(`discard-${line.id}`, [line], 'Discard this row', `The row from ${nameOf(line)} moves to Discarded and will not be imported.`, 'Discarded')}
                        >
                          <Typography variant='body2'>Discard</Typography>
                        </Button>
                      </>
                    )}
                  </ActionRow>
                </TaskItem>
              )
            })}
          </TaskCard>
        )}

        {groups.duplicates.length > 0 && (
          <TaskCard
            number={++taskNumber}
            title={`Check ${plural(countBills(groups.duplicates), 'possible duplicate', 'possible duplicates')}`}
            description='These bills have the same meter and billing period as another bill, and are in the ready list. If one is a revised copy of the other, discard the one it replaces. If they are genuinely different bills, keep both.'
          >
            {groups.duplicates.map((line) => {
              const document = documentsById[line.billDocumentId]
              const otherId = document?.duplicateOfBillDocumentId || null
              const otherInUpload = otherId ? documentsById[otherId] : undefined
              const otherName = otherInUpload?.filename || document?.duplicateOf?.filename || 'another bill'
              const otherLines = otherId ? linesOfBill(otherId) : []
              const where = otherInUpload
                ? 'in this upload'
                : document?.duplicateOf?.uploadedAt ? `already imported, uploaded ${moment(document.duplicateOf.uploadedAt).format('DD/MM/YYYY')}` : 'already imported'
              return (
                <TaskItem key={line.id}>
                  <BillName name={nameOf(line)} summary={lineSummary(line)} note={`Same meter and period as ${otherName} (${where})`} />
                  <ActionRow>
                    <ViewBillButton onClick={() => viewBill(line.billDocumentId)} />
                    {otherId && <ViewBillButton label='View the other bill' onClick={() => viewBill(otherId)} />}
                    {!readOnly && document && (
                      <>
                        <Button
                          size='small'
                          variant='text'
                          disabled={isBusy}
                          onClick={() => run(`keep-${document.id}`, () => fileUploadServiceRef.current.dismissBillDocumentDuplicate(document.id), 'Both bills will be kept')}
                        >
                          <Typography variant='body2'>Keep both</Typography>
                        </Button>
                        <Button
                          size='small'
                          variant='text'
                          disabled={isBusy}
                          onClick={() => discard(`discard-${line.id}`, linesOfBill(document.id), `Discard ${document.filename}`, `Its rows move to Discarded. ${otherName} is kept.`, 'Discarded')}
                        >
                          <Typography variant='body2'>Discard this bill</Typography>
                        </Button>
                        {otherInUpload && otherLines.length > 0 && (
                          <Button
                            size='small'
                            variant='text'
                            disabled={isBusy}
                            onClick={() => discard(`discard-${otherInUpload.id}`, otherLines, `Discard ${otherInUpload.filename}`, `Its rows move to Discarded. ${document.filename} is kept.`, 'Discarded')}
                          >
                            <Typography variant='body2'>Discard the other bill</Typography>
                          </Button>
                        )}
                      </>
                    )}
                  </ActionRow>
                </TaskItem>
              )
            })}
          </TaskCard>
        )}

        {renewableBills.length > 0 && (
          <TaskCard
            number={++taskNumber}
            title={`Confirm renewable electricity on ${plural(renewableBills.length, 'bill', 'bills')}`}
            description='These bills say the electricity was supplied on a renewable tariff backed by certificates. Confirm it and their market-based emissions are recorded as 0. Only confirm if the bill covers these months. A bill you leave is imported on the grid factor.'
            action={!readOnly && renewableJob.confirmable.length > 0 && (
              <LoadingButton
                variant='contained'
                startIcon={<CheckCircleOutline />}
                loading={busyKey === 'renewable-all'}
                loadingPosition='start'
                disabled={isBusy}
                onClick={confirmAllRenewable}
              >
                <Typography>{renewableJob.confirmable.length === 1 ? 'Confirm it' : `Confirm all ${renewableJob.confirmable.length}`}</Typography>
              </LoadingButton>
            )}
          >
            {renewableBills.map((document) => {
              const evidence = document.renewableEvidence
              const electricityRows = lines.filter((line) => line.billDocumentId === document.id && isElectricityLine(line))
              const partial = !coversAllUsage(evidence)
              return (
                <TaskItem key={document.id}>
                  <BillName
                    name={document.filename}
                    summary={electricityRows[0] ? lineSummary(electricityRows[0]) : undefined}
                    note={evidence ? `"${evidence.text}"${evidence.page ? ` (page ${evidence.page})` : ''}` : undefined}
                  />
                  {partial && (
                    <Typography variant='body2' sx={{ mt: 1 }}>
                      {typeof evidence?.coveragePercent === 'number'
                        ? `The certificates cover ${evidence.coveragePercent}% of this bill's usage`
                        : "The bill doesn't say how much of its usage the certificates cover"}, so the rest needs a different factor. Set the rate on the row by hand.
                    </Typography>
                  )}
                  <ActionRow>
                    <ViewBillButton onClick={() => viewBill(document.id)} />
                    {!readOnly && !partial && (
                      <Button size='small' variant='outlined' disabled={isBusy} startIcon={<CheckCircleOutline fontSize='small' />} onClick={() => confirmRenewable(document)}>
                        <Typography variant='body2' sx={{ pl: 0.5 }}>Confirm</Typography>
                      </Button>
                    )}
                    {!readOnly && partial && electricityRows[0] && (
                      <Button size='small' variant='outlined' disabled={isBusy} startIcon={<EditOutlined fontSize='small' />} onClick={() => setEditLine(electricityRows[0])}>
                        <Typography variant='body2' sx={{ pl: 0.5 }}>Edit row</Typography>
                      </Button>
                    )}
                    {!readOnly && (
                      <Button
                        size='small'
                        variant='text'
                        disabled={isBusy}
                        onClick={() => run(`renewable-${document.id}`, () => decideRenewable(document.id, 'dismiss'), 'Marked as not renewable')}
                      >
                        <Typography variant='body2'>Not renewable</Typography>
                      </Button>
                    )}
                  </ActionRow>
                </TaskItem>
              )
            })}
          </TaskCard>
        )}

        {importable.length > 0 && (
          <Paper variant='outlined' sx={{ p: { xs: 2, sm: 3 }, borderRadius: '10px' }}>
            <Button
              variant='text'
              onClick={() => setShowReady(!isReadyOpen)}
              aria-expanded={isReadyOpen}
              endIcon={isReadyOpen ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
              sx={{ px: 0, '&:hover': { bgcolor: 'transparent' } }}
            >
              <Typography variant='subtitle1' sx={{ fontWeight: 500, color: 'text.primary' }}>
                Ready to import ({importable.length.toLocaleString()})
              </Typography>
            </Button>
            <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
              We read these off your bills and they will import as shown. Give them a quick look first.
            </Typography>
            <Collapse in={isReadyOpen}>
              <Box sx={{ mt: 2 }}>
                {!readOnly && (
                  <Stack direction='row' gap={2} justifyContent='flex-end' sx={{ mb: 2 }}>
                    <Button
                      variant='contained'
                      startIcon={<EditOutlined />}
                      disabled={selectedReady.length === 0 || isBusy}
                      onClick={() => setOpenBulkEdit(true)}
                    >
                      <Typography>{`Edit selected (${selectedReady.length})`}</Typography>
                    </Button>
                    <LoadingButton
                      variant='outlined'
                      startIcon={<HighlightOff />}
                      loading={busyKey === 'discard-selected'}
                      loadingPosition='start'
                      disabled={selectedReady.length === 0 || isBusy}
                      onClick={() => discard(
                        'discard-selected',
                        importable.filter((line) => selectedReady.includes(line.id)),
                        `Discard ${plural(selectedReady.length, 'row', 'rows')}`,
                        `${selectedReady.length === 1 ? 'It moves' : 'They move'} to Discarded and will not be imported.`,
                        'Discarded',
                      )}
                    >
                      <Typography>Discard selected</Typography>
                    </LoadingButton>
                  </Stack>
                )}
                <TableDisplay
                  headCells={readyHeadCells}
                  rows={readyRows}
                  selected={selectedReady}
                  setSelected={setSelectedReady}
                  haveCheckboxSelect={!readOnly}
                  selectByCheckboxOnly={true}
                  perPage={readyPerPage}
                  setPerPage={setReadyPerPage}
                  isMobileMode={false}
                  isEmissionMobile={false}
                  isReportActionCard={false}
                />
              </Box>
            </Collapse>
          </Paper>
        )}

        {creditNotes.length > 0 && (
          <Paper variant='outlined' sx={{ p: { xs: 2, sm: 3 }, borderRadius: '10px' }}>
            <Button
              variant='text'
              onClick={() => setShowCreditNotes((open) => !open)}
              aria-expanded={showCreditNotes}
              endIcon={showCreditNotes ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
              sx={{ px: 0, '&:hover': { bgcolor: 'transparent' } }}
            >
              <Typography variant='subtitle1' sx={{ fontWeight: 500, color: 'text.primary' }}>
                Credit notes ({creditNotes.length.toLocaleString()})
              </Typography>
            </Button>
            <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
              A credit note cancels a bill, so it has nothing to import. If we got one wrong, tell us it is a bill.
            </Typography>
            <Collapse in={showCreditNotes}>
              <Box sx={{ mt: 2 }}>
                {creditNotes.map((document) => (
                  <TaskItem key={document.id}>
                    <BillName
                      name={document.filename}
                      summary={document.cancelsBillNumber ? `Cancels bill ${document.cancelsBillNumber}` : undefined}
                    />
                    <ActionRow>
                      <ViewBillButton onClick={() => viewBill(document.id)} />
                      {!readOnly && (document.outstandingRowCount || 0) === 0 && (document.committedRowCount || 0) === 0 && (
                        <Button
                          size='small'
                          variant='text'
                          disabled={isBusy}
                          onClick={async () => {
                            const confirmed = await confirm(
                              'Not a credit note',
                              'This bill will get a row for you to fill in from the document. Anything it was holding back in this upload is released.',
                              'Add a row',
                            )
                            if (confirmed) await run(`row-${document.id}`, () => fileUploadServiceRef.current.addBillDocumentManualRow(document.id), 'Added a row for this bill. Fill in the usage from the bill.')
                          }}
                        >
                          <Typography variant='body2'>Not a credit note</Typography>
                        </Button>
                      )}
                    </ActionRow>
                  </TaskItem>
                ))}
              </Box>
            </Collapse>
          </Paper>
        )}

        {renderFooter && !readOnly && lines.length > 0 && renderFooter(importable.length)}
      </Stack>

      {editLine && (
        <EditUploadLineEmissionDialog
          open={!!editLine}
          closeHandler={() => setEditLine(null)}
          editUploadLine={editLine}
          submitHandler={submitEdit}
          isSubmitting={isEditSubmitting}
        />
      )}
      {openBulkEdit && (
        <EditBulkUploadLinesDialog
          open={openBulkEdit}
          closeHandler={() => setOpenBulkEdit(false)}
          dialogDetailsIds={selectedReady}
          successHandler={() => {
            setOpenBulkEdit(false)
            setSelectedReady([])
            changed()
          }}
          fileType={fileType}
          showMenuItems={BULK_EDIT_MENU_ITEMS[fileType] || BULK_EDIT_MENU_ITEMS['emissions-manual']}
          reportPeriodId={reportPeriodId || undefined}
          create={false}
        />
      )}
    </Box>
  )
}
